import { useNavigate } from '@tanstack/react-router';
import { Copy, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  type CreateSessionParams,
  useCreateSessionMutation,
} from '~entities/session/api/create-session';
import type { Session } from '~entities/session/modal/session';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

function buildCreateSessionParams(
  session: Session,
  name: string,
): CreateSessionParams {
  return {
    name,
    startDate: session.startDate,
    endDate: session.endDate,
    isRegistrationFee: session.isRegistrationFee,
    registrationFeeAmount: session.registrationFeeAmount,
    isClassDefault: session.isClassDefault,
    isEventDefault: session.isEventDefault,
    billingDate: session.billingDate,
    registrationFeePerStudent: session.registrationFeePerStudent,
  };
}

const defaultCopyName = (name: string) => `${name} (Copy)`;

export interface CopySessionButtonProps {
  session: Session;
}

export function CopySessionButton({ session }: CopySessionButtonProps) {
  const navigate = useNavigate();
  const { mutateAsync: createSession, isPending } = useCreateSessionMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newSessionName, setNewSessionName] = useState(() =>
    defaultCopyName(session.name),
  );

  useEffect(() => {
    if (dialogOpen) {
      setNewSessionName(defaultCopyName(session.name));
    }
  }, [dialogOpen, session.name]);

  const handleCopyConfirm = async () => {
    const name = newSessionName.trim();
    if (!name) {
      toast.error('Please enter a name for the new session.');
      return;
    }
    try {
      const params = buildCreateSessionParams(session, name);
      await createSession(params);
      setDialogOpen(false);
      toast.success('Session copied successfully.');

      void navigate({
        to: '/sessions',
      });
    } catch {
      toast.error('Failed to copy session. Please try again.');
    }
  };

  return (
    <>
      <Button
        variant='outline'
        className='size-6 p-0 text-secondary'
        onClick={() => {
          setDialogOpen(true);
        }}
        disabled={isPending}
      >
        {isPending ? <Loader2 className='animate-spin' /> : <Copy />}
      </Button>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Copy session</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new session with the same settings as &quot;
              {session.name}&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='grid gap-2 py-2'>
            <Label htmlFor='copy-session-name'>New session name</Label>
            <Input
              id='copy-session-name'
              value={newSessionName}
              onChange={(e) => {
                setNewSessionName(e.target.value);
              }}
              placeholder='Session name (Copy)'
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleCopyConfirm();
              }}
              disabled={isPending}
            >
              {isPending && <Loader2 className='animate-spin' />}
              Copy session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
